// src/firebase/StaffManagementService.js
import { db } from "./firebase";
import {
  collection,
  doc,
  setDoc,
  getDoc,
  getDocs,
  getCountFromServer,
  query,
  where,
  deleteDoc,
  updateDoc,
  limit,
  startAfter,
  orderBy,
  serverTimestamp,
} from "firebase/firestore";
import { uploadFileToStorage } from "./uploadFileToStorage";
import { createUser } from "./authService";
import {
  generateKeywords,
  generateKeywordsByCharacters,
} from "../components/utils/Helpers";
import { usesLocalApi } from "../services/deploymentConfig";
import { localUserService } from "../services/userLocalApiService";

/* ============================================
   Permission helpers (v2 and legacy support)
============================================ */
const isPlainObject = (v) => v && typeof v === "object" && !Array.isArray(v);

const arrayToPermMap = (arr = []) => {
  const out = {};
  if (!Array.isArray(arr)) return out;
  for (const entry of arr) {
    if (typeof entry === "string") {
      const [moduleKey, action] = entry.split(".");
      if (!moduleKey || !action) continue;
      if (!out[moduleKey]) out[moduleKey] = {};
      out[moduleKey][action] = true;
      continue;
    }
    if (entry && typeof entry === "object") {
      const moduleKey = entry.module;
      const access = Array.isArray(entry.access) ? entry.access : [];
      if (!moduleKey || !access.length) continue;
      if (!out[moduleKey]) out[moduleKey] = {};
      for (const action of access) {
        out[moduleKey][action] = true;
      }
    }
  }
  return out;
};

const permMapToArray = (map = {}) => {
  const out = [];
  if (!isPlainObject(map)) return out;
  for (const [mk, actions] of Object.entries(map)) {
    if (!isPlainObject(actions)) continue;
    for (const [a, val] of Object.entries(actions)) {
      if (val === true) out.push(`${mk}.${a}`);
    }
  }
  return out;
};

const coerceToPermsV2 = (roleData) => {
  if (isPlainObject(roleData?.permissionsV2)) return roleData.permissionsV2;
  if (Array.isArray(roleData?.permissions)) return arrayToPermMap(roleData.permissions);
  return {};
};

const normalizePhonePassword = (value) => String(value || "").trim().replace(/\D/g, "");
const currentHospitalId = () =>
  sessionStorage.getItem("hospitalId") ||
  localStorage.getItem("hospitalId") ||
  sessionStorage.getItem("clearhealthLocalHospitalId") ||
  localStorage.getItem("clearhealthLocalHospitalId") ||
  "";
const appBaseUrl = () =>
  typeof window !== "undefined" && window.location?.origin
    ? window.location.origin
    : "https://clearhealthemr.web.app";

const escapeHtml = (value) =>
  String(value || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");

const buildStaffWelcomeEmail = ({ displayName, hospitalName, email, temporaryPassword, roleName }) => {
  const loginUrl = appBaseUrl();
  const safeName = escapeHtml(displayName || "there");
  const safeHospital = escapeHtml(hospitalName || "your hospital");
  const safeEmail = escapeHtml(email);
  const safePassword = escapeHtml(temporaryPassword);
  const safeRole = escapeHtml(roleName || "Staff");
  const isAdmin = String(roleName || "").toLowerCase().includes("admin");

  const nextSteps = isAdmin
    ? [
        "Sign in with the email and temporary password below.",
        "Change your password when prompted.",
        "Open Settings and confirm hospital details, branding, patient numbering, billing, and security preferences.",
        "Review clinics, departments, roles, and starter services.",
        "Add the remaining hospital users and assign the correct roles before go-live.",
      ]
    : [
        "Sign in with the email and temporary password below.",
        "Change your password when prompted.",
        "Review your assigned role and confirm you can access the modules needed for your work.",
      ];

  return `
    <div style="margin:0;padding:0;background:#f1f5f9;font-family:Arial,Helvetica,sans-serif;color:#0f172a;">
      <div style="max-width:680px;margin:0 auto;padding:28px 16px;">
        <div style="background:#0f766e;border-radius:18px 18px 0 0;padding:26px 28px;color:#ffffff;">
          <div style="font-size:13px;letter-spacing:.08em;text-transform:uppercase;opacity:.85;">ClearHealth EMR</div>
          <h1 style="margin:8px 0 0;font-size:26px;line-height:1.25;">Welcome to ${safeHospital}</h1>
          <p style="margin:8px 0 0;font-size:15px;opacity:.92;">Your account has been created as ${safeRole}.</p>
        </div>

        <div style="background:#ffffff;border:1px solid #e2e8f0;border-top:0;border-radius:0 0 18px 18px;padding:28px;">
          <p style="margin:0 0 16px;font-size:15px;line-height:1.7;">Hello ${safeName},</p>
          <p style="margin:0 0 18px;font-size:15px;line-height:1.7;">
            Your ClearHealth EMR account is ready. Use the credentials below to sign in and complete your setup.
          </p>

          <div style="background:#f8fafc;border:1px solid #e2e8f0;border-radius:14px;padding:18px;margin:20px 0;">
            <div style="font-size:13px;color:#64748b;margin-bottom:6px;">Login email</div>
            <div style="font-size:16px;font-weight:700;color:#0f172a;word-break:break-all;">${safeEmail}</div>
            <div style="height:14px;"></div>
            <div style="font-size:13px;color:#64748b;margin-bottom:6px;">Temporary password</div>
            <div style="display:inline-block;background:#ecfeff;border:1px solid #99f6e4;color:#115e59;border-radius:10px;padding:10px 14px;font-size:18px;font-weight:800;letter-spacing:.04em;">${safePassword}</div>
          </div>

          <h2 style="margin:22px 0 10px;font-size:18px;color:#0f172a;">Next steps</h2>
          <ol style="margin:0 0 22px;padding-left:20px;color:#334155;font-size:15px;line-height:1.75;">
            ${nextSteps.map((step) => `<li>${escapeHtml(step)}</li>`).join("")}
          </ol>

          <p style="margin:24px 0;">
            <a href="${loginUrl}" style="display:inline-block;background:#0f766e;color:#ffffff;text-decoration:none;padding:13px 18px;border-radius:12px;font-weight:700;">Sign in to ClearHealth EMR</a>
          </p>

          <div style="border-top:1px solid #e2e8f0;margin-top:24px;padding-top:16px;font-size:13px;color:#64748b;line-height:1.6;">
            If you did not expect this account, contact your hospital administrator before signing in.
          </div>
        </div>
      </div>
    </div>
  `;
};

/* ============================================
   Staff fetching / searching
============================================ */

/**
 * Fetch paginated staff for a hospital (forward-only with cursor).
 * @param {string} hospitalId
 * @param {number} pageSize
 * @param {import('firebase/firestore').QueryDocumentSnapshot|null} lastDoc
 * @returns {Promise<{staff: any[], lastDoc: any|null, totalUsers: number, hasMore: boolean}>}
 */
export const fetchUsers = async (hospitalId, pageSize = 10, lastDoc = null) => {
  if (usesLocalApi()) {
    return localUserService.fetchUsers(hospitalId, pageSize, lastDoc);
  }

  try {
    if (!hospitalId) throw new Error("Hospital ID is required to fetch staff.");

    const usersCol = collection(db, "users");
    const constraints = [
      where("hospitalId", "==", hospitalId),
      orderBy("createdAt", "desc"),
      limit(pageSize),
    ];
    const qy = lastDoc
      ? query(usersCol, ...constraints, startAfter(lastDoc))
      : query(usersCol, ...constraints);

    const [snap, countSnap] = await Promise.all([
      getDocs(qy),
      getCountFromServer(query(usersCol, where("hospitalId", "==", hospitalId))),
    ]);

    const staff = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    const hasMore = snap.size === pageSize;
    const nextCursor = hasMore ? snap.docs[snap.docs.length - 1] : null;

    return {
      staff,
      lastDoc: nextCursor,
      totalUsers: countSnap.data().count,
      hasMore,
    };
  } catch (error) {
    console.error("Error fetching staff:", error.message);
    throw new Error("Failed to fetch staff. " + error.message);
  }
};

/**
 * Fetch staff by department for a hospital (id + display name).
 */
export const fetchUsersByDepartment = async (hospitalId, departmentId) => {
  if (usesLocalApi()) {
    return localUserService.fetchUsersByDepartment(hospitalId, departmentId);
  }

  try {
    if (!hospitalId || !departmentId) {
      throw new Error("Hospital ID and departmentId are required.");
    }
    const usersCol = collection(db, "users");
    const qy = query(
      usersCol,
      where("hospitalId", "==", hospitalId),
      where("departmentId", "==", departmentId)
    );
    const snap = await getDocs(qy);
    return snap.docs.map((d) => {
      const v = d.data();
      return {
        id: d.id,
        name: `${v.lName || ""} ${v.fName || ""}${v.oName ? ` ${v.oName}` : ""}`.trim(),
        roleName: v.roleName || "",
        phoneNumber: v.phoneNumber || "",
        email: v.email || "",
      };
    });
  } catch (error) {
    console.error("Error fetching staff by department:", error.message);
    throw new Error("Failed to fetch staff by department. " + error.message);
  }
};

/**
 * Keyword search (phone/email/name) with optional role/clinic filters.
 */
export const searchConsultantsByKeyword = async (
  hospitalId,
  searchTerm = "",
  roleId = null,
  clinicId = null
) => {
  if (usesLocalApi()) {
    return localUserService.searchConsultantsByKeyword(hospitalId, searchTerm, roleId, clinicId);
  }

  try {
    if (!hospitalId) throw new Error("Hospital ID is required.");

    const usersRef = collection(db, "users");
    const filters = [where("hospitalId", "==", hospitalId)];
    if (roleId) filters.push(where("roleId", "==", roleId));
    if (clinicId) filters.push(where("clinicId", "==", clinicId));

    const term = (searchTerm || "").trim();
    let snap;
    if (term) {
      const keywords = generateKeywords(term);
      const qy = query(
        usersRef,
        ...filters,
        where("searchKeywords", "array-contains-any", keywords.slice(0, 10)),
        orderBy("createdAt", "desc"),
        limit(200)
      );
      snap = await getDocs(qy);
    } else {
      const qy = query(usersRef, ...filters, orderBy("createdAt", "desc"), limit(200));
      snap = await getDocs(qy);
    }

    const all = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    if (!term) return all;

    // Extra client-side guard for partials
    const t = term.toLowerCase();
    return all.filter((u) => {
      const email = (u.email || "").toLowerCase();
      const phone = (u.phoneNumber || "").toLowerCase();
      const name = `${u.fName || ""} ${u.lName || ""} ${u.oName || ""}`
        .toLowerCase()
        .replace(/\s+/g, " ")
        .trim();
      return email.includes(t) || phone.includes(t) || name.includes(t);
    });
  } catch (error) {
    console.error("❌ Error searching consultants:", error.message);
    return [];
  }
};

/**
 * Fetch staff by clinic for a hospital (id + display name).
 */
export const fetchUsersByClinic = async (hospitalId, clinicId) => {
  if (usesLocalApi()) {
    return localUserService.fetchUsersByClinic(hospitalId, clinicId);
  }

  try {
    if (!hospitalId || !clinicId) {
      throw new Error("Hospital ID and clinicId are required.");
    }
    const usersCol = collection(db, "users");
    const qy = query(
      usersCol,
      where("hospitalId", "==", hospitalId),
      where("clinicId", "==", clinicId)
    );
    const snap = await getDocs(qy);
    return snap.docs.map((d) => {
      const v = d.data();
      return {
        id: d.id,
        name: `${v.lName || ""} ${v.fName || ""}${v.oName ? ` ${v.oName}` : ""}`.trim(),
      };
    });
  } catch (error) {
    console.error("Error fetching staff by clinic:", error.message);
    throw new Error("Failed to fetch staff by clinic. " + error.message);
  }
};

/**
 * Fetch a single user by email (normalized to lowercase).
 * Returns user object or [] if not found (compat with existing callers).
 */
export const fetchUsersByEmail = async (email) => {
  try {
    if (!email || typeof email !== "string" || email.trim() === "") {
      throw new Error("Valid email is required to fetch user.");
    }
    const usersCol = collection(db, "users");
    const qy = query(usersCol, where("email", "==", email.trim().toLowerCase()));
    const snap = await getDocs(qy);
    if (snap.empty) return [];

    const docSnap = snap.docs[0];
    const data = docSnap.data();

    const staff = {
      id: docSnap.id,
      email: data.email || email.trim().toLowerCase(),
      name: `${data.lName || ""} ${data.fName || ""}${data.oName ? ` ${data.oName}` : ""}`.trim(),
      clinicId: data.clinicId || null,
      departmentId: data.departmentId || null,
      departmentName: data.departmentName || null,
      clinicName: data.clinicName || null,
      gender: data.gender || null,
      hospitalId: data.hospitalId || null,
      phoneNumber: data.phoneNumber || null,
      // legacy + v2:
      permissions: Array.isArray(data.permissions) ? data.permissions : permMapToArray(data.permissionsV2 || {}),
      permissionsV2: isPlainObject(data.permissionsV2) ? data.permissionsV2 : arrayToPermMap(data.permissions || []),
      roleId: data.roleId || null,
      roleName: data.roleName || null,
      specialty: data.specialty || null,
      needsPasswordChange: !!data.needsPasswordChange,
      isOwner: !!data.isOwner,
      isSuperAdmin: !!data.isSuperAdmin,
      platformAccess: data.platformAccess || null,
      platformRole: data.platformRole || null,
      canManageFacilities: !!data.canManageFacilities,
      canModifyFacilities: !!data.canModifyFacilities,
      canOnboardFacilities: !!data.canOnboardFacilities,
      canEnterFacilities: !!data.canEnterFacilities,
      assignedHospitalIds: Array.isArray(data.assignedHospitalIds) ? data.assignedHospitalIds : [],
      facilityIds: Array.isArray(data.facilityIds) ? data.facilityIds : [],
    };
    return staff;
  } catch (error) {
    console.error("Error fetching users by email:", error.message);
    throw new Error("Failed to fetch users by email. " + error.message);
  }
};

/* ============================================
   Create / Update user (saves permissionsV2)
============================================ */

/**
 * Create or update a user document.
 * - Uploads passport if provided.
 * - Generates searchKeywords & displayName.
 * - On create, creates auth user + queues welcome email.
 * - Snapshots roleName + permissionsV2 on the user document.
 */
export const handleSaveUser = async (
  hospitalId,
  userData,
  passportFile,
  enrolledBy,
  hospitalDetails
) => {
  if (usesLocalApi()) {
    const user = await localUserService.saveUser(hospitalId, {
      ...userData,
      password: userData.password || normalizePhonePassword(userData.phoneNumber),
    });
    return {
      success: true,
      user,
      message: userData.id ? "Local user updated successfully." : "Local user added successfully.",
    };
  }

  try {
    if (!hospitalId || !userData?.fName || !userData?.lName || !userData?.email) {
      throw new Error("Hospital ID, first name, last name, and email are required.");
    }
    const phonePassword = normalizePhonePassword(userData.phoneNumber);
    if (!userData.id && phonePassword.length !== 11) {
      throw new Error("Enter the user's complete 11-digit phone number. It will be used as the temporary password.");
    }

    // Prepare doc ref (create id up-front to upload photo under it)
    const userRef = userData.id ? doc(db, "users", userData.id) : doc(collection(db, "users"));
    const userId = userData.id || userRef.id;

    // Upload passport (if any)
    const passportPath = passportFile ? `users/passports/${hospitalId}/${userId}` : null;
    const passportURL = passportFile
      ? await uploadFileToStorage(passportFile, passportPath)
      : userData.passport || null;

    // --- Role snapshot (roleName + permissionsV2)
    let roleNameFromRole = null;
    let permissionsV2 = {};
    if (userData.roleId) {
      const roleSnap = await getDoc(doc(db, "roles", userData.roleId));
      if (roleSnap.exists()) {
        const r = roleSnap.data();
        roleNameFromRole = r?.name || null;
        permissionsV2 = coerceToPermsV2(r);
      }
    }
    const roleName = roleNameFromRole || userData.roleName || "";

    // Legacy permissions array (for old code paths)
    const legacyPermissions = permMapToArray(permissionsV2);

    // Derived fields
    const displayName = `${userData.lName || ""} ${userData.fName || ""}${userData.oName ? ` ${userData.oName}` : ""}`.trim();

    const searchKeywords = [
      ...generateKeywordsByCharacters(userData.fName || ""),
      ...generateKeywordsByCharacters(userData.lName || ""),
      ...generateKeywordsByCharacters(userData.oName || ""),
      ...generateKeywords(userData.email || ""),
      ...generateKeywords(userData.phoneNumber || ""),
    ];

    // Normalize base payload (exclude createdAt here; set only on create)
    const payload = {
      id: userId,
      fName: userData.fName,
      lName: userData.lName,
      oName: userData.oName || "",
      gender: userData.gender || "",
      email: (userData.email || "").trim().toLowerCase(),
      phoneNumber: userData.phoneNumber || "",
      passport: passportURL,
      employeeId: userData.employeeId || "",
      clinicId: roleName !== "Super Admin" ? userData.clinicId || "" : "",
      clinicName: userData.clinicName || "",
      roleId: userData.roleId || userData.roleid || "",
      roleName,
      medicalLicenseNumber: userData.medicalLicenseNumber || null,
      departmentId: userData.departmentId || null,
      departmentName: userData.departmentName || null,
      completionDate: userData.completionDate || null,
      employmentType: userData.employmentType || null,
      emergencyContactPhone: userData.emergencyContactPhone || "",
      emergencyContactEmail: (userData.emergencyContactEmail || "").trim().toLowerCase(),
      status: userData.status || "Active",
      specialty: userData.specialty || "",
      hospitalId,
      needsPasswordChange: userData.id ? false : true,
      lastUpdated: serverTimestamp(),
      enrolledBy: enrolledBy || null,

      // 🔐 Permissions snapshot (v2 + legacy)
      permissionsV2,              // ✅ v2 map
      permissions: legacyPermissions, // legacy array for older code paths

      // Search helpers
      searchKeywords,
      displayName,

      // Optional: store some org info for convenience
      hospitalDetails: hospitalDetails || null,
    };

    if (!userData.id) {
      // Create
      await setDoc(userRef, { ...payload, createdAt: serverTimestamp() }, { merge: true });
    } else {
      // Update (do not overwrite createdAt)
      await setDoc(userRef, payload, { merge: true });
    }

    // Create auth user & queue welcome email on first creation
    if (!userData.id) {
      await createUser(payload.email, phonePassword, {
        displayName,
        hospitalId,
      });
      const emailBody = buildStaffWelcomeEmail({
        displayName,
        hospitalName: hospitalDetails?.hospitalName || hospitalDetails?.clinicDetails?.name,
        email: payload.email,
        temporaryPassword: phonePassword,
        roleName,
      });
      await sendEmail(payload.email, `Welcome to ClearHealth EMR - ${hospitalDetails?.hospitalName || "Account Setup"}`, emailBody);
    }

    // Re-read the saved user to return fresh data (timestamps resolved)
    const freshSnap = await getDoc(userRef);
    const freshUser = { id: userRef.id, ...freshSnap.data() };

    return {
      success: true,
      user: freshUser,
      message: userData.id ? "User updated successfully!" : "User added successfully!",
    };
  } catch (error) {
    console.error("Error saving user:", error.message);
    throw new Error("Failed to save user. " + error.message);
  }
};

/* ============================================
   Delete / Status
============================================ */

export const deleteUser = async (userId, hospitalIdOverride = "") => {
  if (usesLocalApi()) {
    const hospitalId = hospitalIdOverride || currentHospitalId();
    const user = await localUserService.setUserStatus(hospitalId, userId, true);
    return { success: true, user, message: "Local user disabled successfully." };
  }

  try {
    if (!userId) throw new Error("User ID is required.");
    await deleteDoc(doc(db, "users", userId));
    return { success: true, message: "User deleted successfully." };
  } catch (error) {
    console.error("Error deleting user:", error.message);
    throw new Error("Failed to delete user.");
  }
};

export const toggleUserStatus = async (userId, status, hospitalIdOverride = "") => {
  if (usesLocalApi()) {
    const hospitalId = hospitalIdOverride || currentHospitalId();
    const disabled = status === "Inactive" || status === "Disabled";
    const user = await localUserService.setUserStatus(hospitalId, userId, disabled);
    return {
      success: true,
      user,
      message: disabled ? "Local user disabled." : "Local user enabled.",
    };
  }

  try {
    if (!userId) throw new Error("User ID is required.");
    await updateDoc(doc(db, "users", userId), {
      status,
      lastUpdated: serverTimestamp(),
    });
    return { success: true, message: `User status updated to ${status}.` };
  } catch (error) {
    console.error("Error toggling user status:", error.message);
    throw new Error("Failed to toggle user status.");
  }
};

export const resetLocalUserPassword = async (hospitalId, userId, password) => {
  if (!usesLocalApi()) {
    throw new Error("Local password reset is only available in OFFLINE or HYBRID mode.");
  }
  await localUserService.resetPassword(hospitalId, userId, password);
  return { success: true, message: "Local user password reset successfully." };
};

/* ============================================
   Email queue (for Cloud Function)
============================================ */

const sendEmail = async (email, subject, html) => {
  try {
    const emailDoc = doc(collection(db, "emailQueue"));
    await setDoc(emailDoc, {
      to: [email],
      message: {
        subject,
        text: html.replace(/<[^>]+>/g, " "),
        html,
      },
    });
  } catch (error) {
    console.error("Error queuing email:", error.message);
  }
};

/* ============================================
   Doctors by specialization
============================================ */

export const fetchDoctorsBySpecialization = async (hospitalId, specialization) => {
  if (usesLocalApi()) {
    return localUserService.fetchDoctorsBySpecialization(hospitalId, specialization);
  }

  try {
    if (!hospitalId || !specialization) {
      throw new Error("Hospital ID and specialization are required.");
    }
    const usersCol = collection(db, "users");
    const qy = query(
      usersCol,
      where("hospitalId", "==", hospitalId),
      where("roleName", "==", "Doctor"),
      where("specialty", "==", specialization)
    );
    const snap = await getDocs(qy);
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  } catch (error) {
    console.error("Error fetching doctors:", error.message);
    throw new Error("Failed to fetch doctors: " + error.message);
  }
};
